import defaultValues from "@/app/features/reports/types/form-default-values";
import convertInspectionToFormValues from "@/app/features/reports/utils/convertInspectionToFormValues";
import { ReportTestItem } from "@/app/features/reports/utils/entities";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { TestSuite } from "store/src/lib/tests/entity";
import { ValidationErrors } from '../types/missing-data';
import filterActiveCustomFields from './filterActiveCustomFields';
import { useDynamicResolver } from './testFormValidator';
import getTestSuiteForSpecification from './getTestSpecification';

export type ValidationResults = {
    values?: Record<string, any>;
    testCase?: ReportTestItem;
    errors: ValidationErrors;
};

export type ValidationResultsMap = Map<number, ValidationResults>;

// Helper function to compare Maps
const mapEquals = (map1: ValidationResultsMap, map2: ValidationResultsMap): boolean => {
    if (map1.size !== map2.size) return false;
    for (const [key, val] of map1) {
        if (!map2.has(key) || JSON.stringify(val) !== JSON.stringify(map2.get(key))) {
            return false;
        }
    }
    return true;
};

type UseValidationResultsReturnType = {
    missingData: Map<number, ValidationErrors>;
    validationResults: ValidationResultsMap;
    revalidateData: () => void;
    isValidationLoading: boolean;
};  

const useValidationResults = (data: ReportTestItem[], testSuites: TestSuite[]): UseValidationResultsReturnType => {

    const resolver = useDynamicResolver(testSuites as any);

    const [validationResults, setValidationResults] = useState<ValidationResultsMap>(new Map());

    const [isValidationLoading, setIsValidationLoading] = useState(false);

    // Use refs to track previous values and detect actual changes
    const dataRef = useRef<ReportTestItem[]>(data);
    const testSuitesRef = useRef<TestSuite[]>(testSuites);
    const prevDataSerializedRef = useRef<string>('');
    const prevTestSuitesSerializedRef = useRef<string>('');

    const fetchValidationResults = useCallback(async (data) => {

        if (!Array.isArray(testSuites) || testSuites.length === 0) {
            setValidationResults(new Map());
            return;
        }

        setIsValidationLoading(true);
        
        try {
            if (!data?.length || !testSuites) return;

            const results = await Promise.all(
                data.map(async (testCase: ReportTestItem) => {
                    const initialData = convertInspectionToFormValues(testCase);
                    const testSuite = getTestSuiteForSpecification(testSuites, testCase.test);

                    if (!testSuite) {
                        console.error('Test suite not found for test case', testSuites, testCase.test);
                        return;
                    }

                    const values = filterActiveCustomFields(initialData, testSuite.suite);

                    const resolverResult = await resolver({
                        ...defaultValues,
                        ...values,
                    }, undefined, {} as any);

                    // console.debug('Resolver result', { values, errors: resolverResult?.errors });

                    return [testCase.assessmentTestId, {
                        ...resolverResult,
                        testCase
                    }] as const;
                })
            );

            const validationResults = new Map<number, ValidationResults>(results.filter(Boolean));

            setValidationResults(prevTests => {
                // Compare stringified maps for equality check
                const prevMap = prevTests instanceof Map ? prevTests : new Map();
                if (mapEquals(prevMap, validationResults)) {
                    return prevTests;
                }
                return validationResults;
            });

        } catch (error) {
            console.error('Error enriching tests:', error);
        } finally {
            setIsValidationLoading(false);
        }

    }, [resolver, testSuites]);

    const revalidateData = useCallback(() => {
        setValidationResults(new Map());
        fetchValidationResults(dataRef.current);
    }, [fetchValidationResults]);

    const missingData = useMemo(() => {
        const result = new Map<number, ValidationErrors>();
        
        validationResults.forEach((validation, id) => {
            const errors = Object.entries(validation.errors).reduce((acc, [type, error]) => {
                if (Array.isArray(error) ? error.length > 0 : !!error) {
                    acc[type] = error;
                }
                return acc;
            }, {} as ValidationErrors);
            if (Object.keys(errors).length > 0) {
                result.set(id, errors);
            }
        });

        console.debug('Missing data', result);
        return result;
    }, [validationResults]);

    useEffect(() => {
        // Serialize data and testSuites to detect actual content changes
        const dataSerialized = JSON.stringify(data);
        const testSuitesSerialized = JSON.stringify(testSuites);

        // Skip if content hasn't actually changed
        if (
            dataSerialized === prevDataSerializedRef.current &&
            testSuitesSerialized === prevTestSuitesSerializedRef.current
        ) {
            return;
        }

        // Update refs with current values
        dataRef.current = data;
        testSuitesRef.current = testSuites;
        prevDataSerializedRef.current = dataSerialized;
        prevTestSuitesSerializedRef.current = testSuitesSerialized;

        if (!data?.length || !testSuites?.length) {
            setValidationResults(new Map());
            return;
        }

        fetchValidationResults(data);
    }, [data, testSuites, fetchValidationResults]);

    return { missingData, validationResults, revalidateData, isValidationLoading };
};

export default useValidationResults;